import { useContext, useEffect, useState } from "react";
import {
  format,
  subDays,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
} from "date-fns";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Calendar } from "../ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { cn } from "../../lib/utils";
import { DateRange } from "react-day-picker";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { UIContext } from "../../providers/UIProvider";

export type DateRangeOption =
  | "custom"
  | "today"
  | "yesterday"
  | "last-week"
  | "last-month"
  | "last-7-days"
  | "last-30-days"
  | "this-month";

export const dateRangeOptions = [
  { value: "custom", text: "Custom" },
  { value: "this-month", text: "This month" },
  { value: "today", text: "Today" },
  { value: "yesterday", text: "Yesterday" },
  { value: "last-week", text: "Last week" },
  { value: "last-month", text: "Last month" },
  { value: "last-7-days", text: "Last 7 days" },
  { value: "last-30-days", text: "Last 30 days" },
];

/**
 * component props
 * @interface CustomDatePickerProps
 */
export interface CustomDatePickerProps {
  onDateRangeChange?: (startDate: Date, endDate: Date) => void;
  defaultDateRangeOption?: DateRangeOption;
  buttonClassName?: string;
  width?: string;
  buttonText?: string;
  startLabel?: string;
  endLabel?: string;
  dateRangeLabel?: string;
  applyButtonText?: string;
  cancelButtonText?: string;
  disabled?: boolean;
}

const CustomDatePicker = ({
  onDateRangeChange,
  defaultDateRangeOption = "this-month",
  buttonClassName = `date-filter-input flex items-center gap-2 max:w-64 cursor-pointer focus:border-[#2684FF] focus:shadow-[0_0_0_1px_#2684FF]`,
  width,
  buttonText,
  startLabel = "Start",
  endLabel = "End",
  dateRangeLabel = "Date range",
  applyButtonText = "Apply",
  cancelButtonText = "Cancel",
  disabled = false,
}: CustomDatePickerProps) => {
  const today = new Date();
  const firstDayOfMonth = startOfMonth(today);
  const lastDayOfMonth = endOfMonth(today);
  const { theme } = useContext(UIContext);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState<DateRangeOption>(
    defaultDateRangeOption
  );
  const [startDate, setStartDate] = useState<Date>(firstDayOfMonth);
  const [endDate, setEndDate] = useState<Date>(lastDayOfMonth);
  const [inputValue, setInputValue] = useState("");
  const [disabledInput, setDisabledInput] = useState(true);
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: firstDayOfMonth,
    to: lastDayOfMonth,
  });
  const [month, setMonth] = useState<Date>(firstDayOfMonth);
  const [isMobile, setIsMobile] = useState(false);

  // Add new state variables for last applied values
  const [lastAppliedRange, setLastAppliedRange] = useState<DateRangeOption>(
    defaultDateRangeOption
  );
  const [lastAppliedStartDate, setLastAppliedStartDate] =
    useState<Date>(firstDayOfMonth);
  const [lastAppliedEndDate, setLastAppliedEndDate] =
    useState<Date>(lastDayOfMonth);
  const [lastAppliedDateRange, setLastAppliedDateRange] = useState<
    DateRange | undefined
  >({
    from: firstDayOfMonth,
    to: lastDayOfMonth,
  });
  const [lastAppliedMonth, setLastAppliedMonth] =
    useState<Date>(firstDayOfMonth);

  // Check screen size for responsive behavior
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    // Initial check
    checkScreenSize();

    // Add event listener for window resize
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const updateDateRange = (start: Date, end: Date) => {
    setStartDate(start);
    setEndDate(end);
    setDateRange({ from: start, to: end });
    updateInputValue(start, end);
  };

  const updateInputValue = (start: Date, end: Date) => {
    setInputValue(
      `${format(start, "MM/dd/yyyy")} - ${format(end, "MM/dd/yyyy")}`
    );
  };

  const handleRangeSelect = (value: DateRangeOption) => {
    setSelectedRange(value);
    const today = new Date();

    if (value === "custom") {
      setDisabledInput(false);
      return;
    }

    setDisabledInput(true);

    switch (value) {
      case "this-month":
        const currentDate = new Date();
        const firstDay = startOfMonth(currentDate);
        const lastDayOfMonth = endOfMonth(endOfMonth(currentDate));
        updateDateRange(firstDay, lastDayOfMonth);
        setMonth(firstDay);
        break;
      case "today":
        updateDateRange(today, today);
        setMonth(today);
        break;
      case "yesterday":
        const yesterday = subDays(today, 1);
        updateDateRange(yesterday, yesterday);
        setMonth(yesterday);
        break;
      case "last-week":
        const monday = startOfWeek(today, { weekStartsOn: 1 });
        const lastMonday = subDays(monday, 7);
        const lastSunday = subDays(monday, 1);
        updateDateRange(lastMonday, lastSunday);
        setMonth(lastMonday);
        break;
      case "last-month":
        const firstDayLastMonth = startOfMonth(subMonths(today, 1));
        const lastDayLastMonth = endOfMonth(subMonths(today, 1));
        updateDateRange(firstDayLastMonth, lastDayLastMonth);
        setMonth(firstDayLastMonth);
        break;
      case "last-7-days":
        updateDateRange(subDays(today, 6), today);
        setMonth(today);
        break;
      case "last-30-days":
        updateDateRange(subDays(today, 29), today);
        setMonth(subDays(today, 15));
        break;
      default:
        break;
    }
  };

  const handleCalendarSelect = (range: DateRange | undefined) => {
    if (range?.from) {
      setSelectedRange("custom");
      setDisabledInput(false);
      setStartDate(range.from);
      setEndDate(range.to || range.from);
      setDateRange(range);
      updateInputValue(range.from, range.to || range.from);
    }
  };

  const resetToLastAppliedState = () => {
    setSelectedRange(lastAppliedRange);
    setStartDate(lastAppliedStartDate);
    setEndDate(lastAppliedEndDate);
    setDateRange(lastAppliedDateRange);
    setMonth(lastAppliedMonth);
    setDisabledInput(lastAppliedRange !== "custom");
    updateInputValue(lastAppliedStartDate, lastAppliedEndDate);
  };

  const handleApply = () => {
    updateInputValue(startDate, endDate);

    // Save the current state as last applied
    setLastAppliedRange(selectedRange);
    setLastAppliedStartDate(startDate);
    setLastAppliedEndDate(endDate);
    setLastAppliedDateRange(dateRange);
    setLastAppliedMonth(month);

    // Notify parent component about the date range change
    if (onDateRangeChange) {
      onDateRangeChange(startDate, endDate);
    }

    setIsOpen(false);
  };

  const handleCancel = () => {
    resetToLastAppliedState();
    setIsOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      resetToLastAppliedState();
    }
    setIsOpen(open);
  };

  const handlePrevMonth = () => {
    setMonth((prevMonth) => {
      const newMonth = new Date(prevMonth);
      newMonth.setMonth(newMonth.getMonth() - 1);
      return newMonth;
    });
  };

  const handleNextMonth = () => {
    setMonth((prevMonth) => {
      const newMonth = new Date(prevMonth);
      newMonth.setMonth(newMonth.getMonth() + 1);
      return newMonth;
    });
  };

  // Initialize with default date range
  useEffect(() => {
    // Set default date range based on the defaultDateRangeOption prop
    handleRangeSelect(defaultDateRangeOption);

    // Initialize last applied values
    setLastAppliedRange(defaultDateRangeOption);
    setLastAppliedStartDate(startDate);
    setLastAppliedEndDate(endDate);
    setLastAppliedDateRange(dateRange);
    setLastAppliedMonth(month);

    updateInputValue(startDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [currentTheme, setCurrentTheme] = useState(theme);
  const isDarkTheme = currentTheme === "dark";

  useEffect(() => {
    const handleStorageChange = () => {
      setCurrentTheme(theme);
    };

    window.addEventListener("storage", handleStorageChange);

    const handleThemeChange = (
      event: CustomEvent<{ theme: "dark" | "light" }>
    ) => {
      setCurrentTheme(event.detail.theme);
    };

    window.addEventListener("themeChange", handleThemeChange as EventListener);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(
        "themeChange",
        handleThemeChange as EventListener
      );
    };
  }, []);

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outlineBlack"
          size="md"
          onClick={() => setIsOpen(true)}
          // className={buttonClassName}
          className={cn(
            isOpen ? "border-[#2684FF] shadow-[0_0_0_1px_#2684FF]" : "",
            buttonClassName
          )}
          disabled={disabled}
        >
          <span className="truncate">{buttonText || inputValue}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          "p-0 BookEZ_card_border_color BookEZ_card_radius shadow-md",
          isDarkTheme ? "!bg-[#191D1E] border-gray-700" : "BookEZ_card_bg"
        )}
        align="start"
        sideOffset={8}
        style={{
          width: width || (isMobile ? "90vw" : "750px"),
          maxWidth: "95vw",
          maxHeight: isMobile ? "60vh" : "none",
          overflowY: isMobile ? "auto" : "visible",
        }}
      >
        <div className={`flex ${isMobile ? "flex-col" : "flex-row"}`}>
          <div
            className={`${isMobile ? "w-full" : "w-[400px]"} ${
              !isMobile &&
              (isDarkTheme
                ? "border-r border-gray-700"
                : "border-r BookEZ_card_border_color")
            }`}
          >
            <div className="relative text-center py-4">
              <button
                className={cn(
                  "absolute left-4 top-1/2 transform -translate-y-1/2 cursor-pointer",
                  isDarkTheme
                    ? "text-gray-400 hover:text-gray-300"
                    : "BookEZ_font_color_gray_2 hover:text-gray-600"
                )}
                onClick={handlePrevMonth}
              >
                <ChevronLeft size={18} />
              </button>
              <span
                className={cn(
                  "font-medium",
                  isDarkTheme ? "text-blue-400" : "text-blue-500"
                )}
              >
                {format(month, "MMMM yyyy")}
              </span>
              <button
                className={cn(
                  "absolute right-4 top-1/2 transform -translate-y-1/2 cursor-pointer",
                  isDarkTheme
                    ? "text-gray-400 hover:text-gray-300"
                    : "BookEZ_font_color_gray_2 hover:text-gray-600"
                )}
                onClick={handleNextMonth}
              >
                <ChevronRight size={18} />
              </button>
            </div>

            <div className="px-5 pb-6 my-2">
              {/* Calendar*/}
              <div>
                <div className="grid grid-cols-7 w-full text-center mb-2">
                  {["S", "M", "T", "W", "T", "F", "S"].map((day, i) => (
                    <div
                      key={i}
                      className={cn(
                        "text-[var(--font_body_size)] font-[var(--font_body_bold_medium)] py-2 w-9 mx-auto",
                        isDarkTheme
                          ? "text-gray-400"
                          : "BookEZ_font_color_gray_1"
                      )}
                    >
                      {day}
                    </div>
                  ))}
                </div>
                <Calendar
                  mode="range"
                  month={month}
                  onMonthChange={setMonth}
                  selected={dateRange}
                  onSelect={handleCalendarSelect}
                  numberOfMonths={1}
                  showOutsideDays={true}
                  defaultMonth={month}
                  modifiers={{
                    range_start: dateRange?.from ? [dateRange.from] : [],
                    range_end: dateRange?.to ? [dateRange.to] : [],
                  }}
                  classNames={{
                    months: "flex flex-col space-y-4",
                    month: "space-y-4",
                    caption: "hidden",
                    head: "hidden",
                    table: "w-full border-collapse",
                    tbody: "w-full",
                    row: "flex w-full mt-2",
                    cell: "text-center relative p-0 mx-auto h-[46px] w-[calc(100%/7)]",
                    day: cn(
                      "h-9 w-9 p-0 mx-auto flex items-center justify-center rounded-full cursor-pointer transition-colors duration-200",
                      isDarkTheme
                        ? "text-gray-200 hover:bg-blue-800/30"
                        : "BookEZ_font_color_gray_2 hover:bg-blue-100"
                    ),
                    day_selected: cn(
                      isDarkTheme
                        ? "text-white font-[var(--font_body_bold_medium)]"
                        : "bg-[var(--calender_selected_bg_color)] text-[var(--calender_selected_color)] font-[var(--font_body_bold_medium)]"
                    ),
                    day_range_start: cn(
                      isDarkTheme
                        ? "bg-blue-600 text-white font-[var(--font_body_bold_large)] relative z-[2] scale-[1.15] shadow-[0_0_0_3px_rgba(37,99,235,0.5)]"
                        : "bg-[var(--calender_selected_bg_color)] font-[var(--font_body_bold_large)] text-white! relative z-[2] scale-[1.15] shadow-[0_0_0_3px_rgba(59,130,246,0.5)]"
                    ),
                    day_range_end: cn(
                      isDarkTheme
                        ? "bg-blue-600 text-white font-[var(--font_body_bold_large)] relative z-[2] scale-[1.15] shadow-[0_0_0_3px_rgba(37,99,235,0.5)]"
                        : "bg-[var(--calender_selected_bg_color)] font-[var(--font_body_bold_large)] text-white! relative z-[2] scale-[1.15] shadow-[0_0_0_3px_rgba(59,130,246,0.5)]"
                    ),
                    day_range_middle: cn(
                      "rounded-full",
                      isDarkTheme
                        ? "bg-[#355277] text-gray-200"
                        : "bg-blue-100 BookEZ_font_color_gray_2"
                    ),
                    day_today: cn(
                      "font-[var(--font_body_bold)] border",
                      isDarkTheme
                        ? "border-blue-500 text-gray-200"
                        : "border-[var(--calender_selected_bg_color)]"
                    ),
                    day_outside: cn(
                      "opacity-60 bg-transparent",
                      isDarkTheme ? "text-gray-600" : "BookEZ_font_color_gray_1"
                    ),
                  }}
                  components={{
                    IconLeft: ({ ...props }) => (
                      <ChevronLeft
                        className={cn(
                          "h-4 w-4",
                          isDarkTheme
                            ? "text-gray-400"
                            : "BookEZ_font_color_gray_2"
                        )}
                      />
                    ),
                    IconRight: ({ ...props }) => (
                      <ChevronRight
                        className={cn(
                          "h-4 w-4",
                          isDarkTheme
                            ? "text-gray-400"
                            : "BookEZ_font_color_gray_2"
                        )}
                      />
                    ),
                  }}
                />
              </div>
            </div>
          </div>

          {/* Right side */}
          <div
            className={`${isMobile ? "w-full min-h-[280px]" : "w-[350px]"} ${
              isMobile &&
              (isDarkTheme
                ? "border-t border-gray-700"
                : "border-t BookEZ_card_border_color")
            } p-5`}
          >
            <div className="mb-4">
              <label
                htmlFor="date-range-select"
                className={cn(
                  "block mb-2 text-sm",
                  isDarkTheme ? "text-gray-300" : "BookEZ_font_color_gray_2"
                )}
              >
                {dateRangeLabel}
              </label>
              <Select
                name="date-range-select"
                value={selectedRange}
                onValueChange={(value) =>
                  handleRangeSelect(value as DateRangeOption)
                }
              >
                <SelectTrigger
                  className={cn(
                    "w-full BookEZ_btn_radius h-10 cursor-pointer",
                    isDarkTheme
                      ? "bg-gray-800 border-gray-700 text-white"
                      : "BookEZ_card_bg border BookEZ_card_border_color"
                  )}
                >
                  <SelectValue placeholder="Custom" />
                </SelectTrigger>
                <SelectContent
                  className={isDarkTheme ? "bg-gray-800 border-gray-700" : ""}
                >
                  {dateRangeOptions.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      className="cursor-pointer"
                    >
                      {option.text}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="mb-4">
              <label
                htmlFor="start-date"
                className={cn(
                  "block mb-2 text-sm",
                  isDarkTheme ? "text-gray-300" : "BookEZ_font_color_gray_2"
                )}
              >
                {startLabel}
              </label>
              <Input
                id="start-date"
                name="start-date"
                disabled={disabledInput}
                value={format(startDate, "MM/dd/yyyy")}
                readOnly
                className={cn(
                  "h-10 cursor-default",
                  isDarkTheme
                    ? "bg-gray-800 border-gray-700 text-white rounded-md"
                    : "BookEZ_card_bg border BookEZ_card_border_color BookEZ_input_border_radius"
                )}
              />
            </div>

            <div className="mb-4">
              <label
                htmlFor="end-date"
                className={cn(
                  "block mb-2 text-sm",
                  isDarkTheme ? "text-gray-300" : "BookEZ_font_color_gray_2"
                )}
              >
                {endLabel}
              </label>
              <Input
                id="end-date"
                disabled={disabledInput}
                name="end-date"
                value={format(endDate, "MM/dd/yyyy")}
                readOnly
                className={cn(
                  "h-10 cursor-default",
                  isDarkTheme
                    ? "bg-gray-800 border-gray-700 text-white rounded-md"
                    : "BookEZ_card_bg border BookEZ_card_border_color BookEZ_input_border_radius"
                )}
              />
            </div>

            <div className="flex gap-3 mt-4">
              <Button
                className={cn(
                  "flex-1 h-10 cursor-pointer",
                  isDarkTheme
                    ? "bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                    : "BookEZ_btn_filled_bg BookEZ_btn_filled_text_color BookEZ_btn_radius"
                )}
                onClick={handleApply}
              >
                {applyButtonText}
              </Button>
              <Button
                className={cn(
                  "flex-1 h-10 bg-transparent cursor-pointer",
                  isDarkTheme
                    ? "text-gray-300 border border-gray-700 hover:bg-gray-800 rounded-md"
                    : "BookEZ_font_color_gray_2 border BookEZ_card_border_color BookEZ_btn_radius"
                )}
                variant="ghost"
                onClick={handleCancel}
              >
                {cancelButtonText}
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default CustomDatePicker;
